/**
 * 客服工单系统 API（对齐 PRD PAGE-PLT-OPS-005）
 * 状态机：OPEN → claim → PENDING → reply/升级 → RESOLVED → close → CLOSED
 * GET    /admin/api/v1/ops/cs/tickets
 * POST   /admin/api/v1/ops/cs/tickets/{ticketId}/claim
 * POST   /admin/api/v1/ops/cs/tickets/{ticketId}/replies
 * POST   /admin/api/v1/ops/cs/tickets/{ticketId}/escalate
 * POST   /admin/api/v1/ops/cs/tickets/{ticketId}/close
 */
import { adminGet, adminPost, USE_MOCK } from './client';

export type TicketStatus = 'OPEN' | 'PENDING' | 'RESOLVED' | 'CLOSED';
export type TicketPriority = 'P0' | 'P1' | 'P2' | 'P3';
type TicketType = 'COMPLAINT' | 'INQUIRY' | 'FAILURE' | 'REQUEST';

export interface TicketItem {
  ticketId: string;
  type: TicketType;
  priority: TicketPriority;
  status: TicketStatus;
  assignee?: string;
  title: string;
  source: string;
  updatedAt: string;
  createdAt?: string;
  replies?: TicketReply[];
}

interface TicketReply {
  replyId: string;
  author: string;
  content: string;
  createdAt: string;
}

type Be = {
  ticketId?: number | string;
  type?: string;
  priority?: string;
  status?: string;
  assignee?: string;
  title?: string;
  source?: string;
  updatedAt?: string;
  createdAt?: string;
};

type BeList = { items?: Be[]; total?: number };

let mock: TicketItem[] = [
  {
    ticketId: 'TKT-9001',
    type: 'FAILURE',
    priority: 'P0',
    status: 'OPEN',
    title: '批量开卡接口超时',
    source: 'MERCHANT',
    updatedAt: '2026-08-05 08:30:00',
    createdAt: '2026-08-05 08:30:00',
  },
  {
    ticketId: 'TKT-9000',
    type: 'COMPLAINT',
    priority: 'P1',
    status: 'PENDING',
    assignee: '客服 小王',
    title: '卡片被误冻结要求紧急解冻',
    source: 'CARDHOLDER',
    updatedAt: '2026-08-05 09:15:00',
    createdAt: '2026-08-05 09:15:00',
  },
  {
    ticketId: 'TKT-8999',
    type: 'INQUIRY',
    priority: 'P2',
    status: 'RESOLVED',
    assignee: '客服 小李',
    title: '授权价调整规则咨询',
    source: 'AGENT',
    updatedAt: '2026-08-04 14:00:00',
    createdAt: '2026-08-04 14:00:00',
  },
  {
    ticketId: 'TKT-8998',
    type: 'REQUEST',
    priority: 'P3',
    status: 'CLOSED',
    assignee: '产品 周理',
    title: '报表增加 CSV 导出功能',
    source: 'INTERNAL',
    updatedAt: '2026-08-03 10:00:00',
    createdAt: '2026-08-03 10:00:00',
  },
];

const TYPE_LABEL: Record<TicketType, string> = {
  COMPLAINT: '投诉',
  INQUIRY: '咨询',
  FAILURE: '故障',
  REQUEST: '需求',
};

function map(b: Be): TicketItem {
  const typeRaw = ((b.type || 'INQUIRY').toUpperCase() as TicketType);
  return {
    ticketId: String(b.ticketId ?? ''),
    type: typeRaw,
    priority: ((b.priority || 'P2').toUpperCase() as TicketPriority),
    status: ((b.status || 'OPEN').toUpperCase() as TicketStatus),
    assignee: b.assignee,
    title: b.title || '',
    source: b.source || 'INTERNAL',
    // 时间展示统一：保留原始 ISO，展示层用 formatDateTime
    updatedAt: b.updatedAt || b.createdAt || '',
    createdAt: b.createdAt || '',
  };
}

function toBe(t: TicketItem): Be {
  return {
    ticketId: t.ticketId,
    type: t.type,
    priority: t.priority,
    status: t.status,
    assignee: t.assignee,
    title: t.title,
    source: t.source,
    updatedAt: t.updatedAt,
    createdAt: t.createdAt,
  };
}

export function ticketTypeLabel(t: TicketType): string {
  return TYPE_LABEL[t] ?? t;
}

export function listTickets(status?: TicketStatus | 'all', priority?: TicketPriority | 'all') {
  const q = new URLSearchParams({ page: '1', size: '50' });
  if (status && status !== 'all') q.set('status', status);
  if (priority && priority !== 'all') q.set('priority', priority);
  return adminGet<BeList>(`/ops/cs/tickets?${q.toString()}`, () => ({
    items: mock.map(toBe),
    total: mock.length,
  })).then((res) => {
    if (!res.ok) return res;
    let items = (res.data.items ?? []).map(map);
    if (status && status !== 'all') items = items.filter((t) => t.status === status);
    if (priority && priority !== 'all') items = items.filter((t) => t.priority === priority);
    return { ok: true as const, data: { items } };
  });
}

function patch(ticketId: string, fn: (t: TicketItem) => TicketItem) {
  mock = mock.map((t) => (t.ticketId === ticketId ? fn(t) : t));
}

export function claimTicket(ticketId: string, assignee: string) {
  return adminPost<unknown>(`/ops/cs/tickets/${ticketId}/claim`, { assignee }, () => {
    patch(ticketId, (t) => ({ ...t, status: 'PENDING', assignee, updatedAt: now() }));
    return {};
  });
}

export function replyTicket(ticketId: string, content: string, author: string) {
  return adminPost<unknown>(`/ops/cs/tickets/${ticketId}/replies`, { content }, () => {
    patch(ticketId, (t) => ({
      ...t,
      updatedAt: now(),
      replies: [
        ...(t.replies ?? []),
        { replyId: `R-${Date.now()}`, author, content, createdAt: now() },
      ],
    }));
    return {};
  });
}

/** 升级（OPEN/PENDING → 高优先级，二次确认前置） */
export function escalateTicket(ticketId: string, reason: string) {
  return adminPost<unknown>(`/ops/cs/tickets/${ticketId}/escalate`, { reason }, () => {
    patch(ticketId, (t) => ({
      ...t,
      priority: t.priority === 'P0' ? 'P0' : t.priority === 'P1' ? 'P0' : 'P1',
      updatedAt: now(),
    }));
    return {};
  });
}

/** 关闭（前置 RESOLVED；PRD 红线：非 RESOLVED 直接关闭须 422） */
export function closeTicket(ticketId: string) {
  return adminPost<unknown>(`/ops/cs/tickets/${ticketId}/close`, {}, () => {
    const hit = mock.find((t) => t.ticketId === ticketId);
    if (hit && hit.status !== 'RESOLVED') {
      throw new Error('仅 RESOLVED 工单可关闭');
    }
    patch(ticketId, (t) => ({ ...t, status: 'CLOSED', updatedAt: now() }));
    return {};
  });
}

function now() {
  // 时间展示统一：返回原始 ISO，展示层用 formatDateTime
  return new Date().toISOString();
}

export { USE_MOCK };
